import { MemberJobType, UpdateMemberJobType, memberJobSchema } from '@/api/mem/memberJob'
import {
    memberJobQueryOptions,
    useUpdateMemberJobMutation,
} from '@/api/mem/memberJobQueryOptions'
import LoadProgress from '@/components/LoadProgress'
import EditDetailAccordion, {
    useEditDetail,
} from '@/components/detail/EditDetailForm'
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid, TextField } from '@mui/material'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { enqueueSnackbar } from 'notistack'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/memberjob_/$uuid'
)({
    component: MemberjobComponent,
})

function MemberjobComponent() {
    const params = Route.useParams()
    const memberJobQuery = useSuspenseQuery(memberJobQueryOptions(params.memberUuid, params.uuid))
    const memberJob = memberJobQuery.data

    const crud = useDetailCrudEvents<UpdateMemberJobType>('memberJob')

    const updateMemberJobMutation = useUpdateMemberJobMutation(
        memberJob.member.uuid,
        crud.onUpdated,
        crud.onError,
    )
    const router = useRouter()

    const onSubmit = (data: MemberJobType) => {
        console.log('onSubmit')
        data.job = { uuid: data.job.uuid }
        updateMemberJobMutation.mutate(data)
        enqueueSnackbar(
            i18next.t('action.changed', { entity: i18next.t('entity.job') }),
        )
        router.history.back()
    }

    const onCancel = () => {
        console.log('onCancel')
        router.history.back()
    }

    const editDetail = useEditDetail({
        onSubmit,
        onCancel,
        resolver: yupResolver<MemberJobType, any, any>(memberJobSchema),
        defaultValues: {
            ...memberJob,
            startDate: memberJob.startDate ? dayjs(memberJob.startDate) : undefined,
            endDate: memberJob.endDate ? dayjs(memberJob.endDate) : undefined,
        },
        title: i18next.t('entity.job'),
    })

    const writeable = memberJob.permissions
        ? memberJob.permissions.some((perm) => perm === 'write')
        : false

    if (memberJobQuery.isLoading || memberJobQuery.isFetching) {
        return <LoadProgress />
    }

    return (
        <EditDetailAccordion {...editDetail} disabled={!writeable}>
            <Grid container spacing={2}>
                <input type="hidden" {...editDetail.register('uuid')} />
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <TextField
                        defaultValue={memberJob.job.name}
                        {...editDetail.register('job.name')}
                        disabled={true}
                        label={i18next.t('entity.job')}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <TextField
                        defaultValue={memberJob.organization.name}
                        {...editDetail.register('organization.name')}
                        disabled={true}
                        label={i18next.t('entity.organization')}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <TextField
                        defaultValue={(memberJob.section && memberJob.section.name) || ''}
                        {...editDetail.register('section.name')}
                        disabled={true}
                        label={i18next.t('entity.section')}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 12, lg: 8 }}>
                    <TextField
                        defaultValue={memberJob.description}
                        {...editDetail.register('description')}
                        disabled={!writeable}
                        label={i18next.t('property.general.comment')}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <ScoregDatePicker
                        name="startDate"
                        defaultValue={
                            memberJob.startDate ? dayjs(memberJob.startDate) : undefined
                        }
                        disabled={!writeable}
                        control={editDetail.control}
                        label={i18next.t('property.general.startDate')}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <ScoregDatePicker
                        name="endDate"
                        defaultValue={
                            memberJob.endDate ? dayjs(memberJob.endDate) : undefined
                        }
                        disabled={!writeable}
                        control={editDetail.control}
                        label={i18next.t('property.general.endDate')}
                    />
                </Grid>
            </Grid>
        </EditDetailAccordion>
    )
}
